<template>
  <Teleport to="body">
    <Transition name="modal">
      <div v-if="modelValue" class="modal-overlay" @click.self="close">
        <div class="modal-container" :class="sizeClass">
          <div class="modal-header">
            <div class="modal-title-section">
              <div v-if="icon" class="modal-icon">
                <vue-feather :type="icon" size="20" />
              </div>
              <h3 class="modal-title">{{ title }}</h3>
            </div>
            <button @click="close" class="modal-close" type="button">
              <vue-feather type="x" size="20" />
            </button>
          </div>
          
          <div class="modal-body">
            <slot></slot>
          </div>
          
          <div class="modal-footer" v-if="$slots.footer || showDefaultFooter">
            <slot name="footer">
              <div v-if="itemId" class="item-id-section" @click="copyId(itemId)" v-tooltip="'Click to copy ID'">
                <vue-feather type="hash" size="14" />
                <span class="item-id-text">{{ itemId }}</span>
                <vue-feather type="copy" size="12" class="copy-icon" />
              </div>
              <div class="footer-buttons">
                <button type="button" class="btn btn-outline-secondary" @click="close" :disabled="loading">
                  Cancel
                </button>
                <button type="button" class="btn btn-primary" @click="$emit('submit')" :disabled="loading || submitDisabled">
                  <span v-if="loading" class="spinner-border spinner-border-sm me-1"></span>
                  {{ submitText }}
                </button>
              </div>
            </slot>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script>
import { useNotifications } from '@/composables/useNotifications'

export default {
  name: 'CreateModal',
  props: {
    modelValue: { type: Boolean, default: false },
    title: { type: String, default: 'Create' },
    icon: { type: String, default: null },
    size: { type: String, default: 'md' },
    loading: { type: Boolean, default: false },
    submitText: { type: String, default: 'Create' },
    submitDisabled: { type: Boolean, default: false },
    showDefaultFooter: { type: Boolean, default: true },
    itemId: { type: String, default: '' }
  },
  emits: ['update:modelValue', 'submit', 'close'],
  setup() {
    const { toast } = useNotifications()
    
    const copyId = async (id) => {
      try {
        await navigator.clipboard.writeText(id)
        toast.success('ID copied to clipboard!')
      } catch (error) {
        toast.error('Failed to copy ID')
      }
    }
    
    return { copyId }
  },
  computed: {
    sizeClass() {
      return `modal-${this.size}`
    }
  },
  methods: {
    close() {
      if (!this.loading) {
        this.$emit('update:modelValue', false)
        this.$emit('close')
      }
    }
  },
  watch: {
    modelValue(val) {
      document.body.style.overflow = val ? 'hidden' : ''
    }
  },
  beforeUnmount() {
    document.body.style.overflow = ''
  }
}
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 48, 73, 0.4);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1050;
  padding: 1rem;
}

.modal-container {
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 25px 50px -12px rgba(0, 48, 73, 0.25);
  width: 100%;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.modal-sm { max-width: 400px; }
.modal-md { max-width: 500px; }
.modal-lg { max-width: 700px; }
.modal-xl { max-width: 900px; }

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid #d9d9d9;
}

.modal-title-section {
  display: flex;
  align-items: center;
  gap: 12px;
}

.modal-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  background: linear-gradient(135deg, #003049 0%, #073b4c 100%);
  border-radius: 8px;
  color: #fff;
}

.modal-title {
  margin: 0;
  font-size: 1.125rem;
  font-weight: 600;
  color: #353535;
}

.modal-close {
  background: none;
  border: none;
  padding: 0.5rem;
  cursor: pointer;
  color: #6b7280;
  border-radius: 8px;
  transition: all 0.2s;
}

.modal-close:hover {
  background: #f8f9fa;
  color: #353535;
}

.modal-body {
  padding: 1.5rem;
  overflow-y: auto;
  flex: 1;
}

.modal-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 1rem 1.5rem;
  border-top: 1px solid #d9d9d9;
  background: #f8f9fa;
}

.footer-buttons {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.item-id-section {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.375rem 0.75rem;
  background: #e5e7eb;
  border-radius: 6px;
  font-size: 0.75rem;
  color: #6b7280;
  cursor: pointer;
  transition: all 0.2s;
  border: 1px solid transparent;
}

.item-id-section:hover {
  background: #d1d5db;
  border-color: #9ca3af;
  color: #374151;
}

.item-id-text {
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  max-width: 180px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.copy-icon {
  opacity: 0.5;
  transition: opacity 0.2s;
}

.item-id-section:hover .copy-icon {
  opacity: 1;
}

/* Dark mode */
:global(body.dark-mode) .modal-container {
  background: #1f2937;
}

:global(body.dark-mode) .modal-header {
  border-color: #374151;
}

:global(body.dark-mode) .modal-title {
  color: #f3f4f6;
}

:global(body.dark-mode) .modal-close {
  color: #9ca3af;
}

:global(body.dark-mode) .modal-close:hover {
  background: #374151;
  color: #f3f4f6;
}

:global(body.dark-mode) .modal-footer {
  background: #111827;
  border-color: #374151;
}

:global(body.dark-mode) .item-id-section {
  background: #374151;
  color: #9ca3af;
}

:global(body.dark-mode) .item-id-section:hover {
  background: #4b5563;
  border-color: #6b7280;
  color: #e5e7eb;
}

/* Transitions */
.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.2s ease;
}

.modal-enter-active .modal-container,
.modal-leave-active .modal-container {
  transition: transform 0.2s ease;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}

.modal-enter-from .modal-container,
.modal-leave-to .modal-container {
  transform: scale(0.95) translateY(-10px);
}
</style>
